import { z } from 'zod';
import { DIMENSIONES_EQ5D5L } from '../escalas/eq5d5l.js';
import { instrumentoSchema } from '../eventos.js';
/**
 * Motor de reglas · el modelo declarativo del semáforo.
 *
 * Una regla es **una banda de puntaje que enciende un color**, y nada más. No
 * lleva acción, no enruta y no avisa: la definición de Fundación del 29/07/2026,
 * ratificada el 06/08, dice que el semáforo es información visual del
 * seguimiento individual y **no recomienda conductas** (`CLAUDE.md` §8,
 * `dev/semaforos-etiquetas.md` §1.1). Por eso el tipo no tiene un campo `accion`:
 * no es que esté vacío, es que agregarlo sería construir lo derogado.
 *
 * **Cinco entradas, cuatro instrumentos.** El semáforo se enciende
 * exclusivamente con las cuatro escalas, pero el EQ-5D-5L aporta dos números
 * independientes —el VAS y el perfil de niveles— y cada uno tiene su propia
 * banda. El índice **no lleva color** y por eso no es una entrada.
 *
 * **Todo es banda, incluido el perfil.** La regla de niveles del EQ-5D-5L —"verde:
 * todas 1 · amarillo: ≥1 en 2–3 y ninguna en 4–5 · rojo: ≥1 en 4–5"— es una banda
 * sobre el **peor nivel** de las cinco dimensiones: peor nivel 1 → verde, 2–3 →
 * amarillo, 4–5 → rojo. Expresarla así no es un truco de implementación: hace que
 * las cinco entradas se evalúen con el mismo código y que un cambio de umbral sea
 * siempre editar dos números, nunca escribir una condición nueva.
 *
 * **Los umbrales son configuración, no código.** Viven en `rule_config`,
 * versionados por tenant y auditados. Los defaults están en `defaults.ts` con la
 * cita de dónde salen; acá está sólo la forma que tienen que respetar.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Las cinco entradas
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Los cinco números que encienden color.
 *
 * `katz15_adl` y no el total: Fundación definió que el semáforo del Katz-15 corre
 * sobre el **subtotal de los ítems 1–6** (email 06/08, punto 3). El total de los
 * quince se registra y se reporta, pero no pinta.
 */
export const entradaDeSemaforoSchema = z.enum([
    'nms',
    'cas_e',
    'katz15_adl',
    'eq5d5l_vas',
    'eq5d5l_niveles',
]);
export const ENTRADAS_DE_SEMAFORO = entradaDeSemaforoSchema.options;
/** De qué instrumento sale cada entrada. El EQ-5D-5L aporta dos. */
export const INSTRUMENTO_DE_LA_ENTRADA = Object.freeze({
    nms: 'nms',
    cas_e: 'cas_e',
    katz15_adl: 'katz15',
    eq5d5l_vas: 'eq5d5l',
    eq5d5l_niveles: 'eq5d5l',
});
/**
 * Rango declarado de cada entrada.
 *
 * Es lo que hace verificable que una configuración esté **completa**: las bandas
 * de una entrada tienen que cubrir su rango entero, sin huecos y sin
 * superposiciones. Un hueco dejaría un puntaje sin color, y un puntaje sin color
 * no es "verde por defecto": es un paciente que el panel no sabe cómo mostrar.
 */
export const RANGO_DE_LA_ENTRADA = Object.freeze({
    nms: { minimo: 0, maximo: 9 },
    cas_e: { minimo: 0, maximo: 6 },
    katz15_adl: { minimo: 0, maximo: 6 },
    eq5d5l_vas: { minimo: 0, maximo: 100 },
    // El peor de los cinco niveles. No hay nivel 0: el instrumento va de 1 a 5.
    eq5d5l_niveles: { minimo: 1, maximo: 5 },
});
/**
 * Si un puntaje más alto es mejor.
 *
 * El Katz-15 corre al revés que los otros tres —más puntaje es **más
 * dependencia**— y confundir la dirección es el error clásico de este dominio.
 * Acá no lo usa el motor (las bandas ya llevan la dirección adentro): lo usa la
 * vista, para saber si la flecha de tendencia es buena o mala noticia.
 */
export const MEJORA_AL_SUBIR = Object.freeze({
    nms: true,
    cas_e: true,
    katz15: false,
    eq5d5l: true,
});
/**
 * Si en cada entrada del semáforo un puntaje más alto es mejor.
 *
 * Es **por entrada y no por instrumento**, porque el EQ-5D-5L aporta dos que
 * corren al revés entre sí: en la escala visual (VAS) más alto es mejor, y en el
 * perfil de niveles el número es el peor nivel declarado, así que más alto es
 * peor. El Katz-15 también va al revés: más puntaje es más dependencia.
 *
 * El motor no la usa —la dirección ya vive en las bandas—: la usa la vista, para
 * decirle al equipo en palabras para qué lado es mejor cada barra.
 *
 * **Convive con `MEJORA_AL_SUBIR`, que es por instrumento**, y las dos tienen que
 * decir lo mismo donde se puedan comparar: en los instrumentos que aportan una
 * sola entrada (NMS, CAS-E y Katz-15). **El EQ-5D-5L es la excepción a propósito**:
 * aporta dos entradas de polaridad opuesta —la escala visual sube para mejor, el
 * perfil de niveles baja—, así que una bandera por instrumento no puede
 * representarlo. Por eso el perfil no lleva flecha de tendencia (`vista.ts`) y la
 * tendencia del EQ-5D-5L se mide sobre el VAS. Un test lo vigila. Sin eso, el
 * Katz-15 en 0 (el mejor resultado posible) se ve como una barra vacía al lado
 * del NMS en 9, que es la barra llena, y las dos son lo mejor que puede pasar.
 */
export const MAS_ALTO_ES_MEJOR = Object.freeze({
    nms: true,
    cas_e: true,
    katz15_adl: false,
    eq5d5l_vas: true,
    eq5d5l_niveles: false,
});
// ─────────────────────────────────────────────────────────────────────────────
// La forma de una configuración
// ─────────────────────────────────────────────────────────────────────────────
/** Los tres colores clínicos. `gris` no está: no es un color del semáforo. */
export const colorDeReglaSchema = z.enum(['verde', 'amarillo', 'rojo']);
export const bandaSchema = z.strictObject({
    /** Identificador estable de la regla. Viaja en el evento y en la auditoría. */
    id: z.string().min(3).max(60),
    /** Extremos **inclusive**, los dos. */
    desde: z.number(),
    hasta: z.number(),
    color: colorDeReglaSchema,
});
export const entradaConfiguradaSchema = z.strictObject({
    entrada: entradaDeSemaforoSchema,
    /**
     * Cuántos días sigue vigente una medición de esta entrada.
     *
     * **`null` es la definición vigente de Fundación**, no una omisión: el color
     * queda *congelado entre hitos* y la UI muestra siempre la última evaluación
     * con su fecha (email 06/08: "entre instancias, se ve el puntaje y color
     * correspondiente a la última evaluación"). El campo existe porque el prompt
     * de la etapa pide la ventana como configuración; ponerle un número sin que
     * Fundación lo defina sería apagarle el semáforo a un paciente que hoy lo
     * tiene encendido.
     */
    ventanaDias: z.number().int().positive().nullable(),
    bandas: z.array(bandaSchema).min(1),
});
/**
 * El estado `gris` de contactabilidad · **apagado**.
 *
 * Lo propuso Ezequiel el 06/08 y es el **único punto abierto del modelo**: falta
 * definir n intentos y ventana. Queda parametrizado para que encenderlo sea
 * cambiar una fila de configuración y no una migración, y queda apagado para que
 * nadie lo vea antes de que exista la definición clínica.
 *
 * Aunque se encienda, **no es un color del semáforo**: es un estado de
 * contactabilidad que viaja aparte. `case_status` nunca vale `gris`.
 */
export const grisSchema = z.strictObject({
    habilitado: z.boolean(),
    /** Intentos del protocolo de silencio tras los cuales hay "ausencia". */
    intentos: z.number().int().positive().nullable(),
    ventanaHoras: z.number().int().positive().nullable(),
});
/**
 * Tendencia · el salto mínimo que mueve la flecha.
 *
 * "Con cambio de ≥1 punto entre mediciones se muestran flechas de tendencia ↑ ↓
 * →" (`semaforos-etiquetas.md` §1.1). Es por instrumento porque un punto de NMS
 * sobre 9 y un punto de VAS sobre 100 no son la misma noticia; hoy los cuatro
 * valen 1, que es lo que dice la definición, y subirle el mínimo al VAS es
 * cambiar un número de configuración el día que Fundación lo pida.
 */
export const tendenciaConfiguradaSchema = z.strictObject({
    // `partialRecord` y no `record`: una configuración a la que le falta un
    // instrumento tiene que **parsear y quedar señalada** por
    // `problemasDeLaConfiguracion`, no explotar al leerla de la base. Un delta
    // faltante no impide calcular un color — se cae al mínimo de un punto, que es
    // la definición— y el chequeo de integridad lo reporta antes de publicar.
    deltaMinimoPorInstrumento: z.partialRecord(instrumentoSchema, z.number().positive()),
});
export const conjuntoDeReglasSchema = z.strictObject({
    /** Versión del conjunto. Es la que viaja en `rule.applied`. */
    version: z.number().int().positive(),
    /** De dónde salen estos valores. Se exige: un umbral clínico sin fuente no entra. */
    fuente: z.string().min(10).max(300),
    entradas: z.array(entradaConfiguradaSchema).min(1),
    gris: grisSchema,
    tendencia: tendenciaConfiguradaSchema,
});
/**
 * Verifica que una configuración sea **total**: cada entrada cubierta entera,
 * exactamente una vez.
 *
 * Es la garantía que permite que `colorDeEntrada` no tenga un caso "sin color".
 * Corre antes de publicar una versión, así que una configuración con un hueco se
 * rechaza en el endpoint y no le llega a ningún paciente.
 */
export function problemasDeLaConfiguracion(conjunto) {
    const problemas = [];
    const declaradas = new Set(conjunto.entradas.map((entrada) => entrada.entrada));
    for (const entrada of ENTRADAS_DE_SEMAFORO) {
        if (!declaradas.has(entrada)) {
            problemas.push({ entrada, problema: 'la entrada no está configurada' });
        }
    }
    const vistas = new Set();
    for (const configurada of conjunto.entradas) {
        if (vistas.has(configurada.entrada)) {
            problemas.push({
                entrada: configurada.entrada,
                problema: 'la entrada está declarada dos veces',
            });
            continue;
        }
        vistas.add(configurada.entrada);
        problemas.push(...problemasDeLasBandas(configurada));
    }
    const ids = conjunto.entradas.flatMap((entrada) => entrada.bandas.map((banda) => banda.id));
    const repetidos = ids.filter((id, indice) => ids.indexOf(id) !== indice);
    for (const id of new Set(repetidos)) {
        problemas.push({ entrada: null, problema: `el id de regla "${id}" está repetido` });
    }
    for (const instrumento of instrumentoSchema.options) {
        if (conjunto.tendencia.deltaMinimoPorInstrumento[instrumento] === undefined) {
            problemas.push({ entrada: null, problema: `falta el delta de tendencia de ${instrumento}` });
        }
    }
    return problemas;
}
function problemasDeLasBandas(configurada) {
    const ordenadas = [...configurada.bandas].sort((una, otra) => una.desde - otra.desde);
    return [
        ...bandasMalFormadas(configurada.entrada, ordenadas),
        ...rangoIncompleto(configurada.entrada, ordenadas),
        ...saltosEntreBandas(configurada.entrada, ordenadas),
    ];
}
/** Una banda al revés o fuera del rango declarado del instrumento. */
function bandasMalFormadas(entrada, ordenadas) {
    const rango = RANGO_DE_LA_ENTRADA[entrada];
    const problemas = [];
    for (const banda of ordenadas) {
        if (banda.hasta < banda.desde) {
            problemas.push({ entrada, problema: `la banda "${banda.id}" va al revés` });
        }
        if (banda.desde < rango.minimo || banda.hasta > rango.maximo) {
            problemas.push({
                entrada,
                problema: `la banda "${banda.id}" se sale del rango ${rango.minimo}–${rango.maximo} de la entrada`,
            });
        }
    }
    return problemas;
}
/** Que las bandas empiecen donde empieza el instrumento y terminen donde termina. */
function rangoIncompleto(entrada, ordenadas) {
    const rango = RANGO_DE_LA_ENTRADA[entrada];
    const problemas = [];
    const primera = ordenadas[0];
    const ultima = ordenadas.at(-1);
    if (primera && primera.desde !== rango.minimo) {
        problemas.push({
            entrada,
            problema: `el rango arranca en ${rango.minimo} y la primera banda en ${primera.desde}`,
        });
    }
    if (ultima && ultima.hasta !== rango.maximo) {
        problemas.push({
            entrada,
            problema: `el rango termina en ${rango.maximo} y la última banda en ${ultima.hasta}`,
        });
    }
    return problemas;
}
/**
 * El paso entre bandas contiguas tiene que ser exactamente 1.
 *
 * Vale para las cuatro entradas enteras y también para el VAS, que el
 * instrumento define como entero de 0 a 100. Un salto de más deja un puntaje sin
 * color; uno de menos, un puntaje con dos.
 */
function saltosEntreBandas(entrada, ordenadas) {
    const problemas = [];
    let anterior;
    for (const actual of ordenadas) {
        if (anterior) {
            if (actual.desde <= anterior.hasta) {
                problemas.push({
                    entrada,
                    problema: `las bandas "${anterior.id}" y "${actual.id}" se superponen`,
                });
            }
            else if (actual.desde !== anterior.hasta + 1) {
                problemas.push({
                    entrada,
                    problema: `entre "${anterior.id}" y "${actual.id}" queda un puntaje sin color`,
                });
            }
        }
        anterior = actual;
    }
    return problemas;
}
/** Las cinco dimensiones, para derivar el peor nivel del perfil. */
export const DIMENSIONES_DEL_PERFIL = DIMENSIONES_EQ5D5L;
/** Los colores, del más severo al menos. El semáforo del caso toma el peor. */
export const SEVERIDAD = Object.freeze(['rojo', 'amarillo', 'verde']);
//# sourceMappingURL=reglas.js.map